import { useActor } from '@xstate/react';
import parsePhoneNumberFromString from 'libphonenumber-js';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { defaultNS, useTranslation } from '@/lib/i18n/';
import { normalizePhoneNumber } from './phone-utils';
import { type PhoneValue, createPhoneInputMachine } from './state.machine';

/**
 * 電話番号入力フック
 *
 * - マシンからのイベント発行を購読
 * - 内部状態とUIイベントを分離
 * - 副作用を最小限に抑制
 */
export const useInputPhone = (value: PhoneValue, onChange: (value: PhoneValue) => void, required: boolean) => {
  const {
    t
  } = useTranslation(defaultNS);

  // 初回レンダリング値の参照を保持
  const initialValueRef = useRef(value);

  // マシンの生成はメモ化
  const machine = useMemo(() => createPhoneInputMachine({
    // oxlint-disable-next-line react/refs -- baseline
    initialValue: initialValueRef.current,
    required,
    t
  }), [required, t]);

  // アクターを使用してマシンを実行
  const [state, send, actor] = useActor(machine);

  // マシンからの値変更イベントを購読
  useEffect(() => {
    const subscription = actor.on('valueChange', event => {
      // このタイミングで先頭の0を付与した値に変更
      // 注意: APIは数字のみを期待するため、normalizePhoneNumber で非数字文字を削除する
      const normalizedInput = normalizePhoneNumber(event.value.phoneNumber);
      const formattedNumber = parsePhoneNumberFromString(`+${event.value.dialCode} ${normalizedInput}`)?.formatNational();
      onChange({
        phoneNumber: formattedNumber ? normalizePhoneNumber(formattedNumber) : normalizedInput,
        dialCode: event.value.dialCode
      });
    });
    return () => subscription.unsubscribe();
  }, [actor, onChange]);
  const {
    formattedNumber,
    selectedDialCode,
    error,
    isOpen,
    searchText,
    countryOptions
  } = state.context;
  const handlePhoneNumberChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    send({
      type: 'PHONE_NUMBER_CHANGE',
      value: e.target.value
    });
  }, [send]);
  const handleDialCodeChange = useCallback((dialCode: string | null) => {
    if (dialCode) {
      send({
        type: 'DIAL_CODE_SELECT',
        dialCode
      });
    }
  }, [send]);
  const handleSearchChange = useCallback((query: string) => {
    send({
      type: 'COUNTRY_SEARCH',
      query
    });
  }, [send]);
  const togglePopover = useCallback(() => {
    send({
      type: 'TOGGLE_POPOVER'
    });
  }, [send]);
  return {
    formattedNumber,
    selectedDialCode,
    error,
    isOpen,
    searchText,
    countryOptions,
    handlePhoneNumberChange,
    handleDialCodeChange,
    handleSearchChange,
    togglePopover
  };
};